/**
 * «Велик» (§2.1) — главный экран. Номер — единственная идентичность: скаут
 * на улице видит велосипед и наклейку «Aida001», а не IMEI и не UUID.
 */
import { useMemo, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useScoutVehicles, useResolveBikeByNumber, displayNumber } from '@/api/vehicles'
import { useRideZones } from '@/api/vehicles'
import { describeBike } from '@/lib/describe-bike'
import { distanceM, formatDistance, hasPosition, useMyPosition } from '@/lib/geo'
import { pushRecentBike, readRecentBikes, type RecentBike } from '@/lib/recent-bikes'
import { BigButton, Chip, Notice } from '@/components/ui'

const NEARBY_LIMIT = 10

export default function HomePage() {
  const navigate = useNavigate()
  const vehicles = useScoutVehicles()
  const zones = useRideZones()
  const me = useMyPosition()
  const resolve = useResolveBikeByNumber()

  const [input, setInput] = useState('')
  const [notFoundFor, setNotFoundFor] = useState<string | null>(null)
  const [ambiguous, setAmbiguous] = useState<string[] | null>(null)
  const [recent, setRecent] = useState<RecentBike[]>(() => readRecentBikes())

  function openBike(id: string, number: string) {
    pushRecentBike({ id, number })
    setRecent(readRecentBikes())
    void navigate(`/bike/${id}`)
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    const value = input.trim()
    if (value === '') return
    setNotFoundFor(null)
    setAmbiguous(null)

    resolve.mutate(
      { input: value, cached: vehicles.data?.vehicles ?? [] },
      {
        onSuccess: (out) => {
          if (out.kind === 'found') {
            const v = vehicles.data?.vehicles.find((x) => x.id === out.id)
            openBike(out.id, v ? displayNumber(v) : value)
          } else if (out.kind === 'ambiguous') {
            setAmbiguous(out.candidates)
          } else {
            setNotFoundFor(value)
          }
        },
      },
    )
  }

  const nearby = useMemo(() => {
    const all = vehicles.data?.vehicles ?? []
    if (!me) return []
    return all
      .filter(hasPosition)
      .map((v) => ({ v, distance: distanceM(me, v) }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, NEARBY_LIMIT)
  }, [vehicles.data, me])

  return (
    <main className="mx-auto w-full max-w-lg p-4 pb-tabs">
      <h1 className="mb-4 text-xl font-semibold">Велик</h1>

      <form onSubmit={submit} className="flex gap-2">
        <input
          aria-label="Номер велика"
          type="text"
          inputMode="numeric"
          value={input}
          onChange={(e) => {
            setInput(e.target.value)
          }}
          placeholder="Номер велика, например 17"
          className="min-h-[56px] flex-1 rounded-xl border border-white/20 bg-transparent
            px-4 font-mono text-2xl"
          autoFocus
        />
        <BigButton type="submit" tone="primary" busy={resolve.isPending} className="flex-none px-6">
          Найти
        </BigButton>
      </form>

      <Link to="/scan" className="mt-2 block">
        <BigButton tone="neutral" type="button">
          Сканировать QR
        </BigButton>
      </Link>

      {notFoundFor ? (
        <div className="mt-3">
          <Notice tone="warn">Велика {notFoundFor} нет в парке</Notice>
        </div>
      ) : null}

      {ambiguous ? (
        <div className="mt-3">
          <Notice tone="warn">
            Несколько байков с таким номером — уточните у диспетчера
            {ambiguous.length > 0 ? `: ${ambiguous.join(', ')}` : ''}
          </Notice>
        </div>
      ) : null}

      <section className="mt-6">
        <h2 className="mb-2 text-sm font-medium uppercase tracking-wide opacity-50">
          Рядом со мной
        </h2>
        {!me ? (
          <p className="text-sm opacity-60">Геопозиция недоступна — разрешите доступ.</p>
        ) : vehicles.isLoading ? (
          <p className="text-sm opacity-60">Загружаем…</p>
        ) : nearby.length === 0 ? (
          <p className="text-sm opacity-60">Рядом ничего нет.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {nearby.map(({ v, distance }) => {
              const status = describeBike(v, zones.data)
              return (
                <li key={v.id}>
                  <button
                    onClick={() => {
                      openBike(v.id, displayNumber(v))
                    }}
                    className="flex min-h-[56px] w-full items-center justify-between gap-2
                      rounded-xl border border-white/10 bg-white/5 px-4 text-left"
                  >
                    <span className="flex items-center gap-2">
                      <span className="font-mono text-lg font-bold">
                        {displayNumber(v) || '—'}
                      </span>
                      <Chip tone={status.tone}>{status.label}</Chip>
                    </span>
                    <span className="shrink-0 text-sm opacity-60">
                      {formatDistance(distance)}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      {recent.length > 0 ? (
        <section className="mt-6">
          <h2 className="mb-2 text-sm font-medium uppercase tracking-wide opacity-50">
            Недавние
          </h2>
          <div className="flex flex-wrap gap-2">
            {recent.map((r) => (
              <button
                key={r.id}
                onClick={() => {
                  openBike(r.id, r.number)
                }}
                className="min-h-11 rounded-full border border-white/20 px-4 font-mono text-sm"
              >
                {r.number || '—'}
              </button>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  )
}
